import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { runAiInspector, type InspectorResult } from "../../api/ai";
import { ApiClientError } from "../../api/types";
import { useAiInspector } from "../../context/AiInspectorContext";
import { useAuth } from "../../context/AuthContext";
import { useAICopilotStore } from "../../ai/copilot";
import { RoleCode } from "../../lib/rbac";

export function AiInspectorPanel() {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const isAdmin = hasRole(RoleCode.SUPER_ADMIN);
  const { inspectorMode, toggleInspectorMode, captureContext } = useAiInspector();
  const location = useLocation();
  const copilotSnapshot = useAICopilotStore((s) => s.screenSnapshot);
  const activeEntity = useAICopilotStore((s) => s.activeEntity);

  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [mode, setMode] = useState<"general" | "ui" | "accounting" | "ide">("general");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InspectorResult | null>(null);

  useEffect(() => {
    if (!inspectorMode) setOpen(false);
  }, [inspectorMode]);

  if (!isAdmin) return null;

  async function submit() {
    const q = question.trim();
    if (!q || loading) return;
    setLoading(true);
    setError(null);
    try {
      const contextJson = {
        ...captureContext(),
        copilot: {
          entity: activeEntity,
          snapshot: copilotSnapshot,
        },
      };
      setResult(
        await runAiInspector({
          question: q,
          mode,
          route: location.pathname,
          contextJson,
        }),
      );
    } catch (err) {
      setResult(null);
      setError(err instanceof ApiClientError ? err.message : t("ai.send_failed"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => {
          toggleInspectorMode();
          if (!inspectorMode) setOpen(true);
        }}
        className={[
          "rounded-lg border px-3 py-1.5 text-xs font-medium transition",
          inspectorMode
            ? "border-amber-400 bg-amber-50 text-amber-900"
            : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
        ].join(" ")}
      >
        🔍 {t("ai.inspector_toggle", { defaultValue: "مفتش الشاشة" })}
      </button>

      {inspectorMode && open && (
        <div
          className="fixed bottom-4 z-[95] w-[min(420px,calc(100vw-2rem))] rounded-2xl border border-amber-200 bg-white shadow-2xl ltr:right-4 rtl:left-4"
          dir="rtl"
        >
          <div className="flex items-center justify-between border-b border-amber-100 px-3 py-2">
            <p className="text-xs font-bold text-amber-900">
              {t("ai.inspector_title", { defaultValue: "AI Inspector — Meta-Dev" })}
            </p>
            <button
              type="button"
              className="text-slate-400 hover:text-slate-700"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>

          <div className="space-y-2 px-3 py-2">
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value as typeof mode)}
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
            >
              <option value="general">عام</option>
              <option value="ui">واجهة / Tailwind</option>
              <option value="accounting">محاسبة / منطق</option>
              <option value="ide">برومبت IDE</option>
            </select>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={3}
              placeholder={t("ai.inspector_placeholder", {
                defaultValue: "ما الخطأ في هذه الشاشة؟ أو لماذا القيد غير متوازن؟",
              })}
              className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs"
            />
            <button
              type="button"
              disabled={loading || !question.trim()}
              onClick={() => void submit()}
              className="w-full rounded-lg bg-amber-500 py-2 text-xs font-bold text-white disabled:opacity-40"
            >
              {loading ? "…" : t("ai.inspector_run", { defaultValue: "تحليل الشاشة" })}
            </button>
            {error && <p className="text-xs text-red-600">{error}</p>}
            {result && (
              <div className="max-h-[40vh] space-y-2 overflow-y-auto text-xs">
                <p className="font-medium text-slate-800">{result.summary}</p>
                {result.findings?.map((f, i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-slate-100 bg-slate-50 px-2 py-1.5"
                  >
                    <span className="font-bold text-amber-800">[{f.severity}]</span> {f.title}
                    <p className="mt-0.5 text-slate-600">{f.detail}</p>
                  </div>
                ))}
                {result.idePrompt && (
                  <details>
                    <summary className="cursor-pointer font-bold text-violet-700">IDE prompt</summary>
                    <pre className="mt-1 whitespace-pre-wrap rounded bg-slate-900 p-2 text-[10px] text-green-100" dir="ltr">
                      {result.idePrompt}
                    </pre>
                  </details>
                )}
                {result.tailwindFixes?.length ? (
                  <details>
                    <summary className="cursor-pointer font-bold">Tailwind</summary>
                    <pre className="mt-1 whitespace-pre-wrap text-[10px]" dir="ltr">
                      {result.tailwindFixes.join("\n\n")}
                    </pre>
                  </details>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
